import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContext } from "../../context/auth.context";
import {
  LocationViewModel,
  SpaceImage,
  useUploadMutation,
} from "../../data/occupancy.api";
import QueryStateWrapper from "../QueryStateWrapper";
import SpaceSlideShow from "./SpaceSlideShow";
import ReactGA from "react-ga4";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  location: LocationViewModel;
};

const UploadSpaceImageModal = ({ isOpen, onClose, location }: Props) => {
  const [imagesToUpload, setImagesToUpload] = useState<FileList | null>();
  const uploadImages = useUploadMutation(location?.key);
  const [uploaded, setUploaded] = useState(false);
  const { userDetails } = useContext(AuthContext);

  useEffect(() => {
    setImagesToUpload(null);
    setUploaded(false);
  }, [isOpen]);

  const spaceImagePreviews = useMemo(() => {
    if (imagesToUpload) {
      return Array.from(imagesToUpload).map(
        (file, i) =>
          ({
            id: i.toString(),
            url: URL.createObjectURL(file),
            authorName: `${userDetails.given_name} ${userDetails.family_name}`,
            authorUsername: userDetails.preferred_username,
            submittedTimeUTC: moment().toISOString(),
            imageType: 0,
          } as SpaceImage)
      );
    }
  }, [imagesToUpload, userDetails]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>Upload Images</ModalHeader>
        <ModalBody>
          {uploaded ? (
            <QueryStateWrapper
              isLoading={uploadImages.isLoading}
              error={uploadImages.error}
              data={uploadImages.data}
            >
              <Alert status="success">
                <AlertIcon />
                Successfully uploaded your image(s)
              </Alert>
            </QueryStateWrapper>
          ) : (
            <>
              {spaceImagePreviews ? (
                <Box mb={4} rounded="xl" overflow="hidden">
                  <SpaceSlideShow images={spaceImagePreviews} />
                </Box>
              ) : null}
              <input
                onChange={(e) => setImagesToUpload(e.target.files)}
                type="file"
                multiple
                accept="image/*"
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex gap={1}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              colorScheme="lancsBlue"
              isDisabled={!imagesToUpload}
              onClick={() => {
                ReactGA.event({
                  category: "user_interaction",
                  action: `upload an image`,
                });
                if (!uploaded) {
                  uploadImages.mutateAsync(imagesToUpload!);
                  setUploaded(true);
                } else {
                  onClose();
                }
              }}
            >
              Confirm
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadSpaceImageModal;
